import React, { useState } from 'react';
import {
  X,
  Heart,
  RotateCw,
  Sparkles,
  Calendar,
  Trash2,
  Edit2,
  Save,
  Tag,
  Check,
} from 'lucide-react';
import { ClothingItem, CategoryType } from '../types';
import { api } from '../lib/api';

interface ItemDetailModalProps {
  item: ClothingItem | null;
  isOpen: boolean;
  onClose: () => void;
  onItemUpdated: (updatedItem: ClothingItem) => void;
  onItemDeleted: (id: string) => void;
  onSendToTryOn: (item: ClothingItem) => void;
}

export const ItemDetailModal: React.FC<ItemDetailModalProps> = ({
  item,
  isOpen,
  onClose,
  onItemUpdated,
  onItemDeleted,
  onSendToTryOn,
}) => {
  if (!isOpen || !item) return null;

  const [isFlipped, setIsFlipped] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  // Edit fields
  const [name, setName] = useState<string>(item.name);
  const [brand, setBrand] = useState<string>(item.brand || '');
  const [size, setSize] = useState<string>(item.size || '');
  const [category, setCategory] = useState<CategoryType>(item.category);
  const [subcategory, setSubcategory] = useState<string>(item.subcategory || '');
  const [color, setColor] = useState<string>(item.color || '');
  const [season, setSeason] = useState<string>(item.season || 'All-Season');
  const [occasion, setOccasion] = useState<string>(item.occasion || 'Casual');
  const [notes, setNotes] = useState<string>(item.notes || '');

  const frontImg = item.image_cropped || item.image_front;
  const backImg = item.image_cropped_back || item.image_back;
  const hasBack = !!backImg;

  // Toggle favorite
  const handleToggleFavorite = async () => {
    try {
      await api.clothes.toggleFavorite(item.id);
      onItemUpdated({
        ...item,
        is_favorite: item.is_favorite ? 0 : 1,
      });
    } catch (err) {
      console.error(err);
    }
  };

  // Log wear count
  const handleLogWear = async () => {
    try {
      await api.clothes.logWear(item.id);
      onItemUpdated({
        ...item,
        wear_count: item.wear_count + 1,
      });
    } catch (err) {
      console.error(err);
    }
  };

  // Save changes
  const handleSave = async () => {
    setIsSaving(true);
    try {
      await api.clothes.update(item.id, {
        name,
        brand,
        size,
        category,
        subcategory,
        color,
        season,
        occasion,
        notes,
      });

      onItemUpdated({
        ...item,
        name,
        brand,
        size,
        category,
        subcategory,
        color,
        season,
        occasion,
        notes,
      });
      setIsEditing(false);
    } catch (err) {
      console.error(err);
    } finally {
      setIsSaving(false);
    }
  };

  // Delete item
  const handleDelete = async () => {
    if (!window.confirm(`Are you sure you want to delete "${item.name}" from your closet?`)) {
      return;
    }
    setIsDeleting(true);
    try {
      await api.clothes.delete(item.id);
      onItemDeleted(item.id);
      onClose();
    } catch (err) {
      console.error(err);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/80 backdrop-blur-md overflow-y-auto">
      <div className="bg-slate-900 border border-slate-800 rounded-3xl w-full max-w-3xl overflow-hidden shadow-2xl my-auto animate-in fade-in zoom-in-95 duration-200 flex flex-col md:flex-row max-h-[90vh]">
        {/* Left: Image Container with 3D Flip */}
        <div className="w-full md:w-1/2 bg-slate-950 p-6 flex flex-col items-center justify-center border-b md:border-b-0 md:border-r border-slate-800 relative">
          {/* Flip Badge */}
          {hasBack && (
            <button
              onClick={() => setIsFlipped(!isFlipped)}
              className="absolute top-4 left-4 z-10 px-3 py-1.5 rounded-xl bg-slate-900/80 hover:bg-slate-800 text-slate-200 border border-slate-700/60 backdrop-blur-md text-xs font-semibold flex items-center gap-1.5 shadow-lg transition-colors"
            >
              <RotateCw className="w-3.5 h-3.5" />
              <span>{isFlipped ? 'View Front' : 'View Back'}</span>
            </button>
          )}

          {/* Favorite heart */}
          <button
            onClick={handleToggleFavorite}
            className="absolute top-4 right-4 z-10 p-2 rounded-xl bg-slate-900/80 hover:bg-slate-800 text-slate-300 border border-slate-700/60 backdrop-blur-md shadow-lg transition-colors"
          >
            <Heart
              className={`w-4 h-4 ${
                item.is_favorite ? 'fill-rose-500 text-rose-500' : 'hover:text-rose-400'
              }`}
            />
          </button>

          {/* Photo Display */}
          <div className="w-full aspect-[4/5] relative perspective-1000 flex items-center justify-center">
            <div
              className={`w-full h-full relative transition-transform duration-500 transform-style-3d ${
                isFlipped ? 'rotate-y-180' : ''
              }`}
            >
              <div className="absolute inset-0 w-full h-full backface-hidden flex items-center justify-center">
                <img
                  src={frontImg}
                  alt={item.name}
                  className="max-w-full max-h-full object-contain drop-shadow-2xl rounded-xl"
                />
              </div>

              {hasBack && (
                <div className="absolute inset-0 w-full h-full backface-hidden rotate-y-180 flex items-center justify-center">
                  <img
                    src={backImg}
                    alt={`${item.name} Back`}
                    className="max-w-full max-h-full object-contain drop-shadow-2xl rounded-xl"
                  />
                </div>
              )}
            </div>
          </div>

          <p className="text-[11px] text-slate-500 mt-2">
            {hasBack ? (isFlipped ? 'Showing Back View' : 'Showing Front View') : 'Single-side view'}
          </p>
        </div>

        {/* Right: Details & Actions */}
        <div className="w-full md:w-1/2 p-6 flex flex-col justify-between overflow-y-auto">
          <div className="space-y-4">
            {/* Header & Close */}
            <div className="flex items-start justify-between">
              <div>
                <span className="text-xs font-bold text-brand-400 uppercase tracking-wider">
                  {item.category}
                </span>
                {isEditing ? (
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full mt-1 px-3 py-1.5 bg-slate-950 border border-slate-800 rounded-lg text-base font-bold text-white focus:outline-none focus:ring-1 focus:ring-brand-500"
                  />
                ) : (
                  <h2 className="text-xl font-extrabold text-white mt-0.5">{item.name}</h2>
                )}
              </div>
              <button
                onClick={onClose}
                className="p-1.5 rounded-xl text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Brand and Size Badges (Prominent) */}
            <div className="flex flex-wrap items-center gap-2 pt-1">
              {isEditing ? (
                <>
                  <input
                    type="text"
                    placeholder="Brand"
                    value={brand}
                    onChange={(e) => setBrand(e.target.value)}
                    className="px-3 py-1 bg-slate-950 border border-slate-800 rounded-xl text-xs text-white"
                  />
                  <input
                    type="text"
                    placeholder="Size"
                    value={size}
                    onChange={(e) => setSize(e.target.value)}
                    className="px-3 py-1 bg-slate-950 border border-slate-800 rounded-xl text-xs text-white"
                  />
                </>
              ) : (
                <>
                  {item.brand && (
                    <span className="px-3 py-1 rounded-xl bg-slate-800 text-white font-extrabold text-xs uppercase tracking-wider border border-slate-700 shadow-sm">
                      🏷️ {item.brand}
                    </span>
                  )}
                  {item.size && (
                    <span className="px-3 py-1 rounded-xl bg-brand-500/20 text-brand-300 font-extrabold text-xs border border-brand-500/30 shadow-sm">
                      📏 Size {item.size}
                    </span>
                  )}
                  {item.fit_type && (
                    <span className="px-2.5 py-1 rounded-xl bg-slate-800/80 text-slate-300 text-xs border border-slate-700">
                      {item.fit_type}
                    </span>
                  )}
                </>
              )}
            </div>

            {/* Garment Meta Grid */}
            <div className="grid grid-cols-2 gap-3 pt-2">
              <div className="p-3 bg-slate-950/60 rounded-xl border border-slate-800/80">
                <span className="text-[11px] font-medium text-slate-400 block">Subcategory</span>
                <span className="text-xs font-semibold text-slate-200">
                  {item.subcategory || 'Standard'}
                </span>
              </div>

              <div className="p-3 bg-slate-950/60 rounded-xl border border-slate-800/80">
                <span className="text-[11px] font-medium text-slate-400 block">Color</span>
                <span className="text-xs font-semibold text-slate-200">
                  {item.color || 'Unspecified'}
                </span>
              </div>

              <div className="p-3 bg-slate-950/60 rounded-xl border border-slate-800/80">
                <span className="text-[11px] font-medium text-slate-400 block">Season</span>
                <span className="text-xs font-semibold text-slate-200">
                  {item.season || 'All-Season'}
                </span>
              </div>

              <div className="p-3 bg-slate-950/60 rounded-xl border border-slate-800/80">
                <span className="text-[11px] font-medium text-slate-400 block">Occasion</span>
                <span className="text-xs font-semibold text-slate-200">
                  {item.occasion || 'Casual'}
                </span>
              </div>
            </div>

            {/* Wear Counter with Log Worn button */}
            <div className="p-3 bg-slate-950/80 rounded-xl border border-slate-800 flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Calendar className="w-4 h-4 text-brand-400" />
                <div>
                  <span className="text-xs font-bold text-white block">
                    Worn {item.wear_count} {item.wear_count === 1 ? 'time' : 'times'}
                  </span>
                  <span className="text-[10px] text-slate-400">Keep track of your wardrobe rotation</span>
                </div>
              </div>

              <button
                onClick={handleLogWear}
                className="px-3 py-1.5 rounded-lg bg-brand-600/20 hover:bg-brand-600/30 text-brand-300 border border-brand-500/30 text-xs font-semibold transition-colors"
              >
                +1 Wore Today
              </button>
            </div>

            {/* Notes / AI Stylist text */}
            {item.notes && (
              <div className="p-3 bg-slate-950/40 rounded-xl border border-slate-800/60">
                <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider block mb-1">
                  Styling Notes
                </span>
                <p className="text-xs text-slate-300 whitespace-pre-line leading-relaxed">
                  {item.notes}
                </p>
              </div>
            )}
          </div>

          {/* Bottom Action Bar */}
          <div className="pt-6 border-t border-slate-800 mt-4 space-y-2.5">
            {/* Try on button */}
            <button
              onClick={() => {
                onSendToTryOn(item);
                onClose();
              }}
              className="w-full py-2.5 rounded-xl bg-gradient-to-r from-brand-600 to-indigo-600 hover:from-brand-500 hover:to-indigo-500 text-white text-xs font-bold shadow-lg shadow-brand-500/20 flex items-center justify-center gap-1.5 transition-all hover:scale-[1.01]"
            >
              <Sparkles className="w-4 h-4" />
              <span>Use in AI Try-On Studio</span>
            </button>

            {/* Edit / Save / Delete Buttons */}
            <div className="flex items-center justify-between gap-2">
              {isEditing ? (
                <button
                  onClick={handleSave}
                  disabled={isSaving}
                  className="flex-1 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold flex items-center justify-center gap-1.5 transition-colors"
                >
                  <Save className="w-3.5 h-3.5" />
                  <span>{isSaving ? 'Saving...' : 'Save Changes'}</span>
                </button>
              ) : (
                <button
                  onClick={() => setIsEditing(true)}
                  className="flex-1 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white text-xs font-semibold flex items-center justify-center gap-1.5 transition-colors"
                >
                  <Edit2 className="w-3.5 h-3.5" />
                  <span>Edit Details</span>
                </button>
              )}

              <button
                onClick={handleDelete}
                disabled={isDeleting}
                className="p-2 rounded-xl bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/30 text-xs font-semibold transition-colors"
                title="Delete from closet"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
